import { useList } from '@refinedev/core';
import type { PublicationListOut } from '@/api/generated/schemas';

/**
 * Publications behind a batch of product ids, keyed by product id.
 *
 * A product id is not a publication id, so `useMany` (which sends `id[in]`)
 * does not fit — publications have to come through the `product_ids` filter.
 * One publication can back two products (ebook + audio), so it is keyed by
 * both; the settlement ledger only ever carries the product id.
 *
 * Every list that resolves a batch of product ids goes through this hook, so
 * the query key stays identical and a page pays for one request.
 */
export const usePublicationsByProducts = (
  productIds: number[]
): Map<number, PublicationListOut> => {
  const { result } = useList<PublicationListOut>({
    resource: 'user_catalog.publications',
    filters: [{ field: 'product_ids', operator: 'in', value: productIds }],
    pagination: { pageSize: Math.max(productIds.length, 1), mode: 'server' },
    queryOptions: { enabled: productIds.length > 0 },
  });

  const publicationByProduct = new Map<number, PublicationListOut>();
  for (const publication of result?.data ?? []) {
    if (publication.ebook?.id != null) {
      publicationByProduct.set(publication.ebook.id, publication);
    }
    if (publication.audio?.id != null) {
      publicationByProduct.set(publication.audio.id, publication);
    }
  }

  return publicationByProduct;
};
